/**
 * First we will load all of this project's JavaScript dependencies which
 * includes Vue and other libraries. It is a great starting point when
 * building robust, powerful web applications using Vue and Laravel.
 */


import debounce from 'lodash/debounce';
import Rellax from 'rellax';
import { gsap, ScrollTrigger, Power4 } from 'gsap/all';
import Vue from 'vue';
import FontFaceObserver from 'fontfaceobserver';
import Headroom from 'headroom.js';
import { SplitText } from "./gsap-bonus/SplitText";

import Router from './router';
import 'lazysizes';

import state from './state';

gsap.registerPlugin(ScrollTrigger);

require('./bootstrap');

require('./components/footer-component');
require('./components/chapter-header-component');
require('./components/header-video-component');
require('./components/faq-component');
require('./components/enquiries-form-component');
require('./components/video-component');
require('./components/columns-component');
require('./components/specs-component');
require('./components/navigation-component');
require('./components/amenities-component');
require('./components/location-google-map-component');
require('./components/slider-component');
require('./components/chapters-map-component');
require('./components/cool-slider-component');
require('./components/reviews-component');
require('./components/event-form-component');
require('./components/availability-component');
// require('./components/inline-slider-component');

/* eslint-disable-next-line no-unused-vars */
const App = new Vue({
    el: '#app',
    delimiters: ['${', '}'],

    data() {
        return {
            firstLoad: true,
            appState: state,
            router: {
                currentSection: null,
                instance: null,
                prefetch: true
            },
            isMobile: false,
            isTablet: false,
            // scrollbar: null,
            showNavigation: false,
            lightPage: '1',
            showFooter: '1',
            letterTls: [],
            splits: [],
            lastVisitedChapter: null,
        };
    },

    watch: {
        isMobile: function () {
            EventBus.$emit('is-mobile-changed');
        },
        isTablet: function () {
            EventBus.$emit('is-tablet-changed');
        },
        showNavigation: function (visibile) {
            EventBus.$emit('navigation-state-changed', visibile);
        },
        lightPage: function (lightPage) {
            console.log(`Light page state: ${lightPage}`);

            if (lightPage === '1') {
                console.log('Switcing to light')
                document.body.classList.remove('dark-page');
                document.body.classList.add('light-page');
                return;
            }

            console.log('Switcing to dark');
            document.body.classList.remove('light-page');
            document.body.classList.add('dark-page');
        }
    },

    mounted() {
        const myElement = document.querySelector('.header__logo-holder');
        const headroom = new Headroom(myElement);
        headroom.init();

        // Prepare router.
        this.router = new Router({
            instance: this,
            prefetch: true,
            section: this.$el.dataset.initialSection,
            ignoreComponents: [
                'footer-component',
                'navigation-component',
                'language-selector'
            ]
        });

        // Prepare instance properties.
        this.showFooter = this.$el.dataset.initialShowFooter;
        this.lightPage = this.$el.dataset.initialLightPage;

        // Initial detect of mobile/tablet breakpoints
        this.detectMobile();
        EventBus.$on('window-resize', () => {
            this.detectMobile();
            // this.splits.forEach((o, i) => {
            //     o.revert();
            // });
        });
        window.addEventListener('resize', this.onWindowResize);

        // Prepare page build and build page.
        EventBus.$on('page-transition-enter-complete', this.buildPage);

        // Liftoff
        Vue.nextTick(() => {
            const fontA = new FontFaceObserver('Baskerville-Reg');
            fontA.load().then(function () {
                EventBus.$emit('page-transition-enter-complete');
            });
        });
    },

    methods: {
        buildPage() {
            if (this.firstLoad) {
                console.log('Still first load?');
                this.intro();
                return;
            }

            this.initRevealTriggers();
            this.initTitleBarReveal();
            this.initSplitText();
            this.splitTitle();

            this.initParallax();
        },

        intro() {
            this.firstLoad = false;
            const introElement = document.getElementById('lottie');

            if (!introElement) {
                EventBus.$emit('page-transition-enter-complete');
                return;
            }

            document.getElementById('intro-animation').classList.remove('hidden');
            gsap.set('.header__logo', { opacity: 0 });

            const intro = bodymovin.loadAnimation({
                container: introElement,
                path: '/data/logo-animation-v04.json',
                renderer: 'svg',
                loop: false,
                autoplay: false
            })

            let firstPlay = true;
            let el; let logoEl; let box; let headerLogo;

            const play = () => {
                intro.removeEventListener('DOMLoaded', play);
                logoEl = document.querySelector('#lottie svg g[clip-path="url(#__lottie_element_4)"]');
                el = document.querySelector('#lottie svg g[mask="url(#__lottie_element_16)"]').nextElementSibling;
                headerLogo = document.querySelector('.header__logo').getBoundingClientRect();

                window.requestAnimationFrame(() => {
                    intro.play();
                });
            }

            intro.addEventListener('DOMLoaded', play)
            intro.addEventListener('complete', () => {
                if (!firstPlay) {
                    return;
                }

                firstPlay = false;
                box = el.getBoundingClientRect();
                console.log(headerLogo.width);
                const scaleFactor = box.width / headerLogo.width;
                gsap.set('.header__logo', { y: box.top - headerLogo.top, scale: scaleFactor, opacity: 1, transformOrigin: 'top center' });
                gsap.set(logoEl, {
                    opacity: 0
                });

                intro.setDirection(-1);
                intro.play();

                gsap.set('#intro-animation', { clip: `rect(0, ${window.innerWidth}px, ${window.innerHeight}px, 0)` });
                gsap.to('#intro-animation', {
                    duration: 1.1,
                    delay: 1,
                    transformOrigin: 'top center',
                    clip: `rect(0px, ${window.innerWidth}px, ${0}px, 0px)`,
                    ease: 'power4.easeOut'
                });


                gsap.to('.header__logo', {
                    delay: 1, duration: 1.1,
                    y: 0,
                    scale: 1,
                    transformOrigin: 'top center',
                    ease: 'power4.easeOut'
                });

                gsap.delayedCall(1.2, () => {
                    EventBus.$emit('page-transition-enter-complete');
                });
            });
        },

        initParallax() {
            const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
            const rellax = [...document.querySelectorAll('.rellax')];

            function initialize() {
                rellax.forEach((object) => {
                    if (!object.dataset.initialVisible) {
                        object.classList.add('rellax--initialized');
                    }
                });
            }

            if (viewportWidth > 768) {
                if (rellax.length > 0) {
                    new Rellax('.rellax', { // eslint-disable-line
                        center: true,
                        callback: initialize
                    });
                }

                return;
            }

            initialize();
        },

        initRevealTriggers() {
            const elements = [
                ...document.querySelectorAll('.a-fade-up'),
                ...document.querySelectorAll('.a-fade-in'),
                ...document.querySelectorAll('[data-reveal]'),
                ...document.querySelectorAll('.opacity-blur'),
                ...document.querySelectorAll('.square-button__link')
            ];

            elements.forEach((element) => {
                const { revealDelay, revealManually, revealPosition } = element.dataset;
                if (revealManually === "") {
                    return;
                }

                if (revealPosition) {
                    console.log(revealPosition);
                }

                const startPercentage = revealPosition || '88%';


                gsap.timeline({
                    scrollTrigger: {
                        trigger: element,
                        start: `top ${startPercentage}`,
                        onEnter: () => {
                            if (!revealDelay) {
                                element.classList.add('--reveal');
                                return;
                            }

                            gsap.delayedCall(revealDelay, () => {
                                element.classList.add('--reveal');
                            });
                        }
                    },
                });
            });
        },

        initTitleBarReveal() {
            const elements = [...document.querySelectorAll('.a-reveal')];

            elements.forEach((element) => {
                gsap.timeline({
                    scrollTrigger: {
                        trigger: element,
                        start: "top 88%",
                        onEnter: () => {
                            gsap.delayedCall(0.4, () => {
                                element.classList.add('a-revealed');
                            });
                        }
                    },
                });
            });
        },

        splitTitle() {
            const elements = [...document.querySelectorAll('.split-title')];

            gsap.delayedCall(1, () => {
                const fadeIn = [...document.querySelectorAll('.a-fade-in--initial')];
                for (let index = 0; index < fadeIn.length; index++) {
                    const element = fadeIn[index];
                    element.classList.add('--reveal');
                }
                const playButton = document.getElementById('play-button');
                if (playButton) {
                    playButton.classList.add('--reveal');
                }
            });

            for (let index = 0; index < elements.length; index++) {
                const element = elements[index];

                gsap.set(element, { "box-sizing": "border-box" });
                element.dataset.scrollIndex = index;
                const splitLines = new SplitText(element, { type: 'lines,chars', linesClass: 'line' });
                const { chars, lines } = splitLines;

                gsap.set(chars, { opacity: 0, y: '100%', transformOrigin: 'bottom center' });
                gsap.set(element, { autoAlpha: 1 });

                this.splits.push(splitLines);

                for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
                    const line = lines[lineIndex];
                    gsap.to(line.children, {
                        scrollTrigger: {
                            trigger: element,
                            start: 'top 88%',
                            once: true,
                        },
                        y: '0%',
                        duration: 1,
                        stagger: 0.06,
                        ease: Power4.easeOut,
                        opacity: 1,
                        onComplete: () => {
                            // splitLines.revert();
                        }
                    });
                }
            }
        },

        initSplitText() {
            const bodyElements = [
                ...document.querySelectorAll('.split-text-sub p'),
                ...document.querySelectorAll('.split-text-sub h2'),
                ...document.querySelectorAll('.split-text-sub h3')
            ];

            for (let index = 0; index < bodyElements.length; index++) {
                const element = bodyElements[index];
                element.dataset.scrollIndex = index;
                const mySplitText = new SplitText(element, { type: "lines" });
                const { lines } = mySplitText; // an array of all the divs that wrap each character

                this.splits.push(mySplitText);

                gsap.set(lines, { opacity: 0, y: '100%', rotationX: -90, transformOrigin: 'bottom center' });
                gsap.set(element, { autoAlpha: 1 });

                gsap.to(lines, {
                    scrollTrigger: {
                        trigger: element,
                        start: 'top 88%',
                        once: true,
                    },
                    duration: 1.2,
                    ease: Power4.easeOut,
                    y: '0%',
                    stagger: 0.06,
                    opacity: 1,
                    rotationX: 0,
                    onComplete: () => {
                        // mySplitText.revert();
                    }
                });
            }


            const listItems = [...document.querySelectorAll('.split-text-sub li')];

            for (let index = 0; index < listItems.length; index++) {
                const element = listItems[index];
                element.dataset.scrollIndex = index;

                gsap.set(element, { opacity: 0, y: '100%', rotationX: -90, transformOrigin: 'bottom center' });
                // gsap.set(element, { autoAlpha: 1 });

                gsap.to(element, {
                    scrollTrigger: {
                        trigger: element,
                        start: 'top 88%',
                        once: true,
                    },
                    duration: 1.2,
                    ease: Power4.easeOut,
                    y: '0%',
                    stagger: 0.06,
                    opacity: 1,
                    rotationX: 0,
                    onComplete: () => {
                        // mySplitText.revert();
                    }
                });
            }

            const titles = [...document.querySelectorAll('.split-text')];

            for (let index = 0; index < titles.length; index++) {
                const element = titles[index];
                element.dataset.scrollIndex = index;
                const mySplitText = new SplitText(element, { type: "lines" });
                const { lines } = mySplitText; // an array of all the divs that wrap each character

                this.splits.push(mySplitText);

                gsap.set(lines, { opacity: 0, y: '100%', rotationX: -90, transformOrigin: 'bottom center' });
                gsap.set(element, { autoAlpha: 1 });

                gsap.to(lines, {
                    scrollTrigger: {
                        trigger: element,
                        start: 'top 88%',
                        once: true,
                    },
                    y: '0%',
                    stagger: 0.06,
                    opacity: 1,
                    rotationX: 0,
                    onComplete: () => {
                        // mySplitText.revert();
                    }
                });
            }
        },

        /**
         * Raised when the client window is resizing.
         *
         * @return {Void}
         */
        onWindowResize: debounce(() => {
            EventBus.$emit('window-resize');
        }, 150),

        detectMobile() {
            const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

            if (viewportWidth < 768) {
                this.isTablet = false;
                this.isMobile = true;
                return;
            }

            if (viewportWidth < 1025) {
                this.isTablet = true;
                this.isMobile = false;
                return;
            }

            this.isMobile = false;
            this.isTablet = false;
        },

        onPillarMouseEnter(event) {
            const element = event.currentTarget;
            if (document.documentElement.classList.contains('no-touchevents')) {
                const parent = element.parentElement;

                gsap.set(parent.querySelectorAll('.u-parallax-holder'), { opacity: 0.65 });
                gsap.set(parent.querySelectorAll('.u-parallax-holder img'), { scale: 1.1 });
            }
        },

        onPillarMouseLeave(event) {
            const element = event.currentTarget;
            if (document.documentElement.classList.contains('no-touchevents')) {
                const parent = element.parentElement;

                gsap.set(parent.querySelectorAll('.u-parallax-holder'), { opacity: '' })
                gsap.set(parent.querySelectorAll('.u-parallax-holder img'), { scale: 1 });
            }
        },

        linkToContactForm() {
            const form = document.getElementById("form");
            zenscroll.center(form, 500, 200);
        }
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.onWindowResize);
    }
});
